<template src="./vc-pinboard.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { callTurnToConfigFn, encodeArray } from 'util/turnto-utils'
import domUtils from 'util/dom-utils'
import getMediaForVcPinboardService from 'services/api/get-media-for-vc-pinboard'
import MasonryLayout from 'layouts/masonry/MasonryLayout.vue'
import pinboardConfig from './vc-pinboard.config'
import PinboardItem from 'components/pinboard/VcPinboardItem.vue'
import pinboardStore from 'store/vc-pinboard-store'
import trackingService from 'services/tracking-service'
import visibleObserver from 'util/visible-observer'

export default {
  data() {
    return {
      hasMore: true,
      isLoading: false,
      items: [],
      offset: 0,
      pinboardStore
    }
  },
  components: {
    'tt-masonry-layout': MasonryLayout,
    'tt-pinboard-item': PinboardItem
  },
  computed: {
    currentFilters() {
      return {
        skus: this.pinboardStore.skus,
        tags: this.pinboardStore.tags,
        brands: this.pinboardStore.brands
      }
    },
    displayShowMoreButton() {
      return !pinboardConfig.progressiveLoading && !this.isLoading &&
        this.offset > 0 && this.hasMore
    },
    displayShowMoreWrapper() {
      return this.items.length > 0 && (this.displayShowMoreButton || this.isLoading)
    },
    pinboardConfig() {
      return pinboardConfig
    },
    useProgressiveLoading() { // for MasonryLayout.  If true, it will handle scroll event
      return this.hasMore && pinboardConfig.progressiveLoading && !this.isLoading
    }
  },
  methods: {
    doneLoading() { // called when MasonryLayout is done positioning tiles that were loaded
      this.isLoading = false
    },
    fetchData() {
      if (this.hasMore && !this.isLoading) {
        this.isLoading = true
        getMediaForVcPinboardService.getMediaForVcPinboard({
          offset: this.offset,
          limit: pinboardConfig.limit,
          activeOnly: pinboardConfig.activeOnly,
          brands: encodeArray(pinboardStore.brands),
          skus: encodeArray(pinboardStore.skus),
          tags: encodeArray(pinboardStore.tags),
          sortOrder: pinboardConfig.sortOrder,
          includeReviewData: pinboardConfig.showReviewStars,
          minCaptionCharCount: pinboardConfig.minCaptionCharCount,
          minCaptionWordCount: pinboardConfig.minCaptionWordCount
        }).then((data) => {
          if (data.media.length === 0) {
            // if there are items the MasonryLayout will call this.doneLoading() to set to false
            this.isLoading = false
          }
          // grabbing the last few ids to make sure we don't put a dup item in from this batch.
          // this can happen when an item gets mediated out in between page loads.  Yes, unlikely,
          // but happened in prod with previous version causing some layout issues.
          const ids = this.items.slice(-10).map(item => item.id)
          data.media.forEach((item) => {
            if (!~ids.indexOf(item.id)) {
              this.items.push(item)
            }
          })
          this.hasMore = data.total > this.offset + data.media.length

          // only called on first load
          if (this.offset === 0) {
            const eventProps = {
              skus: pinboardStore.skus,
              tgs: pinboardStore.tags,
              bs: pinboardStore.brands
            }
            trackingService.queueEvent('vc-pinboard', 'load', null, eventProps)
            const rootElem = this.$el
            visibleObserver.register(rootElem, () => {
              trackingService.queueEvent('vc-pinboard', 'view', null, eventProps)
              visibleObserver.unregister(rootElem)
            })
            nextTick(() => { callTurnToConfigFn('vcPinboard', 'onFinish') })
          }
          this.offset += data.media.length
        }).catch(() => this.isLoading = false)
      }
    },
    openModal(e, mediaIndex) {
      // eslint-disable-next-line
      import(/* webpackChunkName: 'media-modal' */ 'components/media-modal/media-modal-loader').then((mediaModalLoader) => {
        mediaModalLoader.default.load({
          loadMoreCb: this.fetchData,
          media: this.items,
          returnFocusElm: domUtils.closest(e.target, '.tt-c-vc-pin'),
          startingIndex: mediaIndex,
          moreToLoad: () => this.hasMore
        })
      })
    },
    showMore() {
      this.fetchData()
    }
  },
  watch: {
    // whenever one of the filters changes (skus, tags, or brands), we need to reload the pinboard
    currentFilters: {
      handler() {
        this.offset = 0
        this.hasMore = true
        this.items = []
        this.fetchData()
      },
      deep: true
    }
  },
  created() {
    this.fetchData()
  }
}
</script>

<style lang="scss" src="./vc-pinboard.scss"></style>
